import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Hammer, FileImage, FileText, AlertCircle } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import AttachmentPreview from '@/components/AttachmentPreview';

const BASE = process.env.REACT_APP_BACKEND_URL;

export default function VendorPortalPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState(null);
  const [activeTab, setActiveTab] = useState('drawings');

  useEffect(() => {
    setLoading(true);
    axios.get(`${BASE}/api/vendor-portal/${token}`)
      .then((res) => setData(res.data))
      .catch((err) => {
        const status = err.response?.status;
        if (status === 404) setError('Invalid or revoked link. Please contact the studio for a new one.');
        else setError('Unable to load portal. Please try again.');
      })
      .finally(() => setLoading(false));
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFAF8]">
        <div className="w-8 h-8 border-2 border-[#3A8C8C] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFAF8] px-4" data-testid="vendor-portal-error">
        <div className="glass-card rounded-2xl p-8 max-w-md text-center">
          <AlertCircle size={32} className="text-[#C4727F] mx-auto mb-3" />
          <h1 className="text-base font-semibold text-[#2C2C2E] font-['Manrope']">Access Denied</h1>
          <p className="text-sm text-[#6B7280] mt-2">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAF8]" data-testid="vendor-portal">
      {/* Top bar */}
      <div className="bg-white border-b border-[#E8E6E1]">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#3A8C8C]/10 flex items-center justify-center">
              <Hammer size={18} className="text-[#3A8C8C]" />
            </div>
            <div>
              <h1 className="text-base font-semibold text-[#2C2C2E] font-['Manrope']" data-testid="vendor-portal-vendor">{data.vendor_name}</h1>
              <p className="text-[10px] text-[#6B7280]">Vendor portal · view-only access</p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-[10px] text-[#6B7280] uppercase tracking-wider">Project</p>
            <p className="text-sm font-semibold text-[#2C2C2E]" data-testid="vendor-portal-project">{data.project_name}</p>
            {data.client_name && <p className="text-[10px] text-[#6B7280]">{data.client_name}</p>}
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-6">
        {/* Tabs */}
        <div className="flex gap-1 bg-[#E8E6E1]/50 rounded-lg p-0.5 w-fit mb-4">
          {[
            { id: 'drawings', label: 'Drawings', icon: FileText, count: data.totals.drawings },
            { id: 'photos', label: 'Photos', icon: FileImage, count: data.totals.photos },
          ].map((t) => {
            const Icon = t.icon;
            return (
              <button
                key={t.id}
                onClick={() => setActiveTab(t.id)}
                className={`flex items-center gap-1.5 px-3.5 py-1.5 rounded-md text-xs font-medium transition-colors ${activeTab === t.id ? 'bg-white shadow-sm text-[#2C2C2E]' : 'text-[#6B7280] hover:text-[#2C2C2E]'}`}
                data-testid={`vendor-portal-tab-${t.id}`}
              >
                <Icon size={13} /> {t.label}
                <Badge className="bg-[#E8E6E1] text-[#6B7280] text-[9px] ml-1">{t.count}</Badge>
              </button>
            );
          })}
        </div>

        {/* Drawings */}
        {activeTab === 'drawings' && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="vendor-portal-drawings">
            {data.drawings.length === 0 ? (
              <div className="col-span-full glass-card rounded-2xl p-8 text-center">
                <FileText size={28} className="text-[#D4D0C8] mx-auto mb-2" />
                <p className="text-xs text-[#6B7280]">No drawings shared on this project</p>
              </div>
            ) : data.drawings.map((d) => (
              <button
                key={d.id}
                onClick={() => setPreview(d)}
                className="glass-card rounded-xl p-4 text-left hover:shadow-md transition-shadow group"
                data-testid={`vendor-portal-drawing-${d.id}`}
              >
                <div className="w-10 h-10 rounded-lg bg-[#3A8C8C]/10 flex items-center justify-center mb-2 group-hover:bg-[#3A8C8C]/20 transition-colors">
                  <FileText size={16} className="text-[#3A8C8C]" />
                </div>
                <p className="text-xs font-medium text-[#2C2C2E] truncate">{d.name || 'Drawing'}</p>
                {d.category && <p className="text-[10px] text-[#6B7280] mt-0.5">{d.category}</p>}
              </button>
            ))}
          </div>
        )}

        {/* Photos */}
        {activeTab === 'photos' && (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="vendor-portal-photos">
            {data.photos.length === 0 ? (
              <div className="col-span-full glass-card rounded-2xl p-8 text-center">
                <FileImage size={28} className="text-[#D4D0C8] mx-auto mb-2" />
                <p className="text-xs text-[#6B7280]">No photos shared on this project</p>
              </div>
            ) : data.photos.map((ph) => (
              <button
                key={ph.id}
                onClick={() => setPreview(ph)}
                className="aspect-square rounded-xl overflow-hidden bg-[#E8E6E1] hover:ring-2 hover:ring-[#3A8C8C] transition-all relative group"
                data-testid={`vendor-portal-photo-${ph.id}`}
              >
                {ph.data_url ? (
                  <img src={ph.data_url} alt={ph.name} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center"><FileImage size={24} className="text-[#D4D0C8]" /></div>
                )}
                {ph.folder && (
                  <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/60 to-transparent p-2">
                    <p className="text-[10px] text-white truncate">{ph.folder}</p>
                  </div>
                )}
              </button>
            ))}
          </div>
        )}
      </div>

      <AttachmentPreview attachment={preview} onClose={() => setPreview(null)} />
    </div>
  );
}
